/**
 * Phase F — Service lifecycle control buttons.
 *
 * Renders within AppBar:
 *   - Pre-breakfast → "Start Breakfast" button (yellow)
 *   - Post-breakfast → "Reset" button (with confirmation)
 *
 * Reset preserves waiting_sessions (per AGENT_HANDOFF Warning #4).
 */
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useStartBreakfast, useResetApp } from "@/hooks/useTableMutations";
import useSelectionStore from "@/store/useSelectionStore";

export function StartBreakfastButton() {
  const start = useStartBreakfast();
  const clearSelection = useSelectionStore((s) => s.clear);

  const handleClick = () => {
    start.mutate(undefined, {
      onSuccess: () => clearSelection(),
    });
  };

  return (
    <button
      data-testid="start-breakfast-btn"
      onClick={handleClick}
      disabled={start.isPending}
      className="px-4 py-1.5 rounded-md font-semibold text-sm disabled:opacity-50 transition-opacity"
      style={{ backgroundColor: "#F5C518", color: "#1A1A1A" }}
    >
      {start.isPending ? "Starting…" : "Start Breakfast"}
    </button>
  );
}

export function ResetButton() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const reset = useResetApp();
  const clearSelection = useSelectionStore((s) => s.clear);

  const handleConfirm = () => {
    reset.mutate(undefined, {
      onSuccess: () => {
        clearSelection();
        setConfirmOpen(false);
      },
    });
  };

  return (
    <>
      <button
        data-testid="reset-btn"
        onClick={() => setConfirmOpen(true)}
        className="px-4 py-1.5 rounded-md font-semibold text-sm border transition-colors hover:bg-gray-50"
        style={{ borderColor: "#D1D5DB", color: "#374151" }}
      >
        Reset
      </button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent
          data-testid="reset-confirm-dialog"
          className="w-[calc(100%-2rem)] max-w-[440px] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Reset breakfast service?</AlertDialogTitle>
            <AlertDialogDescription>
              All reservations and seated tables will be cleared. Dynamic tables
              will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel" disabled={reset.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-confirm"
              onClick={handleConfirm}
              disabled={reset.isPending}
              style={{ backgroundColor: "#DC2626" }}
            >
              {reset.isPending ? "Resetting…" : "Reset"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
